import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { DEFAULT_ENTRY_LIMITS } from '../../../../../constants/form.constants';
import { ITableConfig, ITableRow } from '../../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { TranslateService } from '../../../../../translate/translate.service';
import { AccessibilityAuditToolNames, AuditToolNameToAuditType } from '../../../../../../../shared/constants/audit-tool';
import { IScanAudit } from '../../../../../../../shared/interfaces/scan-audit.interface';
import {
  ISummaryRequestQuery,
  IUserFlowScanPointSummary,
  IUserFlowScanPointSummaryServerResponse,
} from '../../../../../../../shared/interfaces/scan-summaries.interface';
import { NgbTableUtilities } from '../../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { $scanSummaries } from '../../../../../../../shared/constants/scan-summaries';
import { PageSummariesService } from '../../../../../services/page-summaries.service';
import { IAudit } from '../../../../../../../shared/interfaces/audit.interface';
import { $scanAudit } from '../../../../../../../shared/constants/scan-audit';
import { $paginationParams } from '../../../../../constants/pagination-params';
import { CommonUtility } from '../../../../../utility/common.utility';
import { IPaginationParams } from '../../../../../interfaces/pagination.interface';
import { AngularUtility } from '../../../../../utility/angular.utility';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { Api, ApiQueryOption } from '../../../../../../../shared/constants/api';
import { $page } from '../../../../../../../shared/constants/page';
import { FeatureFlagService } from '../../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';

enum TableColumn {
  SCAN_POINT = 'scan_point',
  URL = 'url',
  PAGE_SCORE = 'score',
  OPEN = 'open',
  DISMISSED = 'dismissed',
  FAILED = 'failed',
  REQUIRES_REVIEW = 'requires_review',
}

@Component({
  selector: 'app-view-scan-results-scan-points',
  templateUrl: './view-scan-results-scan-points.component.html',
  standalone: false,
})
export class ViewScanResultsScanPointsComponent implements OnInit, OnChanges {
  private readonly defaultPaginationParams: IPaginationParams = {
    [$paginationParams.pageNumber]: 1,
    [$paginationParams.pageSize]: DEFAULT_ENTRY_LIMITS[1],
    [$paginationParams.sortBy]: undefined,
    [$paginationParams.direction]: undefined,
  };
  private summaryData$: Observable<IUserFlowScanPointSummaryServerResponse>;
  private paginationParams$: BehaviorSubject<IPaginationParams> = new BehaviorSubject<IPaginationParams>(null);

  public readonly DEFAULT_ENTRY_LIMITS: readonly number[] = DEFAULT_ENTRY_LIMITS;
  public tableConfig: ITableConfig;
  public tableData$: Observable<ITableRow[]>;
  public totalSummaries$: Observable<number>;
  public searchTerm: string = '';
  public getSummariesRequest$: BehaviorSubject<void> = new BehaviorSubject<void>(null);

  @Input() public workspaceId: string;
  @Input() public digitalPropertyId: string;
  @Input() public scanId: string;
  @Input() public scanAudit: IScanAudit;
  @Input() public auditTool: AccessibilityAuditToolNames;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location,
    private translateService: TranslateService,
    private pageSummariesService: PageSummariesService,
    private featureFlagService: FeatureFlagService,
  ) {}

  public get searchLabel(): string {
    return this.featureFlagService.variation(FeatureFlagCollection.userFlowsQ42023Revamp, false)
      ? 'search_by_page_name'
      : 'search_by_state_name';
  }

  public get page(): number {
    return this.paginationParams$.value[$paginationParams.pageNumber];
  }

  public set page(page: number) {
    this.paginationParams$.next({
      ...this.paginationParams$.value,
      [$paginationParams.pageNumber]: page,
    });
  }

  public get pageSize(): number {
    return this.paginationParams$.value[$paginationParams.pageSize];
  }

  public set pageSize(pageSize: number) {
    this.paginationParams$.next({
      ...this.paginationParams$.value,
      [$paginationParams.pageNumber]: 1,
      [$paginationParams.pageSize]: pageSize,
    });
  }

  private buildTableRow(summary: IUserFlowScanPointSummary): ITableRow {
    const audit: IAudit = this.scanAudit[$scanAudit.audits][AuditToolNameToAuditType[this.auditTool]];

    return {
      originalData: summary,
      data: {
        [TableColumn.SCAN_POINT]: NgbTableUtilities.linkCell({
          text: summary[$scanSummaries.scanPoint][$page.name],
          attributes: {
            routerLink: `./${Api.scan_point_report}/${summary[$scanSummaries.documentId]}/${Api.pages}/${
              summary[$scanSummaries.scanPoint][$page._id]
            }`,
            queryParams: {
              [Api.toolName]: this.auditTool,
            },
          },
        }),
        [TableColumn.URL]: NgbTableUtilities.textCell({
          text: String(summary[$scanSummaries.url]),
        }),
        [TableColumn.PAGE_SCORE]: NgbTableUtilities.scoreCell({
          score: Math.floor(summary[$scanSummaries.score]),
        }),
        [TableColumn.OPEN]: NgbTableUtilities.textCell({
          text: String(this.pageSummariesService.getOpenIssues(summary, audit)),
        }),
        [TableColumn.DISMISSED]: NgbTableUtilities.textCell({
          text: String(this.pageSummariesService.getDismissedIssues(summary, audit)),
        }),
        [TableColumn.FAILED]: NgbTableUtilities.textCell({
          text: String(this.pageSummariesService.getTotalFailedIssues(summary, audit)),
        }),
        [TableColumn.REQUIRES_REVIEW]: NgbTableUtilities.textCell({
          text: String(this.pageSummariesService.getTotalReviewIssues(summary, audit)),
        }),
      },
    };
  }

  private getTableConfig(): ITableConfig {
    return {
      columns: {
        [TableColumn.SCAN_POINT]: {
          translationKey: this.featureFlagService.variation(FeatureFlagCollection.userFlowsQ42023Revamp, false)
            ? 'table_column_page_name'
            : 'table_column_state',
          styles: {
            maxWidth: '25%',
            width: '25%',
          },
        },
        [TableColumn.URL]: {
          translationKey: 'label_url',
          styles: {
            maxWidth: '14rem',
            width: '25%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        [TableColumn.PAGE_SCORE]: {
          translationKey: 'page_score',
        },
        [TableColumn.OPEN]: {
          translationKey: 'open',
        },
        [TableColumn.DISMISSED]: {
          translationKey: 'dismissed',
        },
        [TableColumn.FAILED]: {
          translationKey: 'tab_failed',
        },
        [TableColumn.REQUIRES_REVIEW]: {
          translationKey: 'label_requires_review',
        },
      },
      caption: this.translateService.instant('scan_results_states_table_caption'),
      emptyState: {
        iconId: 'magnifying-glass-2',
        title: this.translateService.instant('no_search_results_available'),
        subtitle: this.translateService.instant('no_search_results_available_description'),
      },
    };
  }

  private getSummaryData$(): Observable<IUserFlowScanPointSummaryServerResponse> {
    return combineLatest([this.paginationParams$, this.getSummariesRequest$]).pipe(
      filter(([params]: [IPaginationParams, void]) => SharedCommonUtility.notNullish(params)),
      tap(this.updateUrlQueryParams.bind(this)),
      switchMap(([params]: [IPaginationParams, void]) => {
        const query: ISummaryRequestQuery = {
          [ApiQueryOption.toolName]: this.auditTool,
          page: String(params[$paginationParams.pageNumber]),
          limit: String(params[$paginationParams.pageSize]),
          scanPointName: this.searchTerm?.trim() ?? '',
        };

        return this.pageSummariesService.getPageSummariesForUserFlowScanPoints(
          this.workspaceId,
          this.digitalPropertyId,
          this.scanId,
          query,
        );
      }),
      AngularUtility.shareRef(),
    );
  }

  private updateUrlQueryParams(params: IPaginationParams): void {
    const url: string = this.router
      .createUrlTree([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          [$paginationParams.pageNumber]: params[$paginationParams.pageNumber],
          [$paginationParams.pageSize]: params[$paginationParams.pageSize],
        },
        queryParamsHandling: 'merge',
      })
      .toString();

    this.location.replaceState(url);
  }

  private updatePaginationParams(): void {
    const queryParams: Params = this.activatedRoute.snapshot.queryParams;
    const paginationParams: IPaginationParams = { ...this.defaultPaginationParams };

    if (CommonUtility.isValidPaginationQueryParams(queryParams)) {
      paginationParams[$paginationParams.pageNumber] = Number(queryParams[$paginationParams.pageNumber]);
      paginationParams[$paginationParams.pageSize] = Number(queryParams[$paginationParams.pageSize]);
    }

    this.paginationParams$.next(paginationParams);
  }

  ngOnInit(): void {
    this.tableConfig = this.getTableConfig();
    this.summaryData$ = this.getSummaryData$();
    this.tableData$ = this.summaryData$.pipe(
      map((data: IUserFlowScanPointSummaryServerResponse) => data[$scanSummaries.summaries].map(this.buildTableRow.bind(this))),
    );
    this.totalSummaries$ = this.summaryData$.pipe(
      map((data: IUserFlowScanPointSummaryServerResponse) => data[$scanSummaries.total]),
    );
    this.updatePaginationParams();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.auditTool?.isFirstChange()) {
      this.getSummariesRequest$.next();
    }
  }
}
